/* HOYA delivery terms shared by the cart drawer (city block and checkout controls), the checkout hand-off and the
   cart page. Every number comes live from Deco Garden's zone calculator; this file holds no prices. */
(() => {
  if (window.HoyaZoneTerms) return;

  const SOURCE = 'https://delivery-zones-manager.vercel.app';
  // A basket value above every threshold: it only identifies a city's zone and terms. Never shown or charged.
  const PROBE_TOTAL = 100000000;
  const ZONES_KEY = 'hoya-delivery-zones';
  const ZONES_AGE = 600000;
  const TERMS_KEY = 'hoya-zone-terms:v1';
  const TERMS_AGE = 1800000;
  const ADDRESS_KEY = 'hoya:delivery:v1';
  const LEGACY_ADDRESS_KEY = 'hoya-delivery-address';
  const TRANSIENT = new Set([408, 425, 429, 500, 502, 503, 504]);
  const SERVED = new Set(['ok', 'partial_restricted']);
  const CLOSED = new Set(['no_zone', 'no_delivery_zone']);
  const zonesRequests = new Map();

  const cityKey = value => String(value || '').trim().replace(/\s+/g, ' ');
  const fill = (text, values) => Object.entries(values).reduce((result, [key, value]) => result.split(`{${key}}`).join(String(value)), String(text || ''));
  const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));
  const abortError = () => {
    const error = new Error('ABORTED');
    error.name = 'AbortError';
    return error;
  };

  function pause(milliseconds, signal) {
    if (!signal) return wait(milliseconds);
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        signal.removeEventListener('abort', aborted);
        resolve();
      }, milliseconds);
      const aborted = () => {
        clearTimeout(timeout);
        reject(abortError());
      };
      signal.addEventListener('abort', aborted, { once: true });
    });
  }

  async function requestJson(path, options = {}, policy = {}) {
    const attempts = Math.max(1, Number(policy.attempts) || 1);
    const timeoutMs = Math.max(1, Number(policy.timeout) || 8000);
    const callerSignal = options.signal;
    for (let attempt = 0; attempt < attempts; ++attempt) {
      if (callerSignal?.aborted) throw abortError();
      const controller = new AbortController();
      const forwardAbort = () => controller.abort();
      callerSignal?.addEventListener('abort', forwardAbort, { once: true });
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs);
      try {
        const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        if (!response.ok) {
          const error = new Error(`HTTP_${response.status}`);
          error.status = response.status;
          throw error;
        }
        return await response.json();
      } catch (error) {
        const callerAborted = !!callerSignal?.aborted;
        const transient = timedOut || ['TypeError', 'SyntaxError'].includes(error?.name) || TRANSIENT.has(Number(error?.status));
        if (callerAborted || !transient || attempt + 1 >= attempts) throw error;
        await pause(250 * (attempt + 1), callerSignal);
      } finally {
        clearTimeout(timeout);
        callerSignal?.removeEventListener('abort', forwardAbort);
      }
    }
    throw new Error('REQUEST_UNAVAILABLE');
  }

  function loadZones(source = SOURCE) {
    if (zonesRequests.has(source)) return zonesRequests.get(source);
    try {
      const cached = JSON.parse(sessionStorage.getItem(ZONES_KEY) || 'null');
      if (cached?.source === source && Date.now() - cached.time < ZONES_AGE && Array.isArray(cached.zones)) {
        const request = Promise.resolve(cached.zones);
        zonesRequests.set(source, request);
        return request;
      }
    } catch (_) {}
    const request = requestJson(`${source}/api/zones`, { credentials: 'omit', cache: 'no-store' }, { attempts: 2, timeout: 5000 })
      .then(config => {
        if (!Array.isArray(config?.zones)) throw new Error('ZONES_UNAVAILABLE');
        const zones = config.zones.map(zone => ({
          name: String(zone.name ?? ''),
          enabled: zone.deliveryEnabled !== false,
          cost: Number(zone.deliveryCost),
          free: Number(zone.freeDeliveryAbove),
          minDays: Number(zone.minDays),
          maxDays: Number(zone.maxDays)
        }));
        try {
          sessionStorage.setItem(ZONES_KEY, JSON.stringify({ source, time: Date.now(), zones }));
        } catch (_) {}
        return zones;
      })
      .catch(error => {
        zonesRequests.delete(source);
        throw error;
      });
    zonesRequests.set(source, request);
    return request;
  }

  // Zones sharing a name are only trusted when they also agree on every term shown.
  function agreeing(matches) {
    if (!matches.length) return null;
    const [first] = matches;
    return matches.every(zone => zone.cost === first.cost && zone.free === first.free && zone.minDays === first.minDays && zone.maxDays === first.maxDays) ? first : null;
  }

  function zoneByName(zones, name, cost, free) {
    if (!Array.isArray(zones)) return null;
    const enabled = zones.filter(zone => zone.enabled);
    const exact = enabled.filter(zone => zone.name === name);
    const named = exact.length ? exact : enabled.filter(zone => zone.name.trim() === name.trim());
    return agreeing(named.filter(zone => zone.cost === cost && zone.free === free));
  }

  // The source words delivery time as "1 עד 6 ימי עסקים"; labels: { days_range, days_exact, days_one }.
  function daysText(zone, labels) {
    const { minDays: min, maxDays: max } = zone || {};
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < 1 || max < min) return '';
    if (min < max) return fill(labels.days_range, { min, max });
    return max === 1 ? labels.days_one : fill(labels.days_exact, { max });
  }

  // Follows the store's own money format (the same one Liquid's money filter prints).
  function formatMoney(cents, format = '{{amount}}') {
    const amount = Number(cents) / 100;
    const number = (decimals, thousands, decimal) => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => ({
      amount: number(2, ',', '.'),
      amount_no_decimals: number(0, ',', '.'),
      amount_with_comma_separator: number(2, '.', ','),
      amount_no_decimals_with_comma_separator: number(0, '.', ','),
      amount_with_apostrophe_separator: number(2, "'", '.'),
      amount_no_decimals_with_space_separator: number(0, ' ', '.'),
      amount_with_space_separator: number(2, ' ', ','),
      amount_with_period_and_space_separator: number(2, ' ', '.')
    })[kind] ?? number(2, ',', '.')).trim();
  }

  // One calculator answer. terms are in shekels, as the calculator sends them.
  function classify(data) {
    // A missing or blank price is unknown, never ₪0.
    const raw = data?.deliveryCost;
    const cost = typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '') ? Number(raw) : NaN;
    if (data?.allowed === true && SERVED.has(data.reason) && Number.isFinite(cost) && cost >= 0) {
      const free = Number(data.freeDeliveryAbove);
      const smallFee = Number(data.smallOrderFee);
      const smallThreshold = Number(data.smallOrderThreshold);
      return {
        state: 'served',
        canonicalCity: typeof data.canonicalCity === 'string' ? cityKey(data.canonicalCity) : '',
        terms: {
          zoneName: String(data.zoneName ?? '').trim(),
          cost,
          free: Number.isFinite(free) && free > 0 ? free : 0,
          small: Number.isFinite(smallFee) && smallFee > 0 && Number.isFinite(smallThreshold) && smallThreshold > 0 ? { fee: smallFee, threshold: smallThreshold } : null
        }
      };
    }
    if (CLOSED.has(data?.reason)) return { state: 'closed', canonicalCity: '', terms: null };
    return { state: 'unknown', canonicalCity: '', terms: null };
  }

  // Delivery price in agorot for a basket worth `basis` agorot (item prices before discounts, as the carrier sees them).
  function fee(terms, basis) {
    if (!terms) return null;
    if (terms.free > 0 && basis >= terms.free * 100) return 0;
    if (terms.small && terms.cost > 0 && basis < terms.small.threshold * 100) return Math.round(terms.small.fee * 100);
    return Math.round(terms.cost * 100);
  }

  // The fee now, the next cheaper step and the distance to free delivery (all agorot).
  function tiers(terms, basis) {
    const now = fee(terms, basis);
    if (now === null) return null;
    const regular = Math.round(terms.cost * 100);
    const next = terms.small && regular > 0 && basis < terms.small.threshold * 100 && regular < now
      ? { missing: terms.small.threshold * 100 - basis, fee: regular }
      : null;
    const freeMissing = now > 0 && terms.free > 0 && basis < terms.free * 100 ? terms.free * 100 - basis : null;
    const goal = next ? terms.small.threshold * 100 : freeMissing !== null ? terms.free * 100 : null;
    return { fee: now, next, freeMissing, ratio: goal ? Math.max(0, Math.min(1, basis / goal)) : 1 };
  }

  // "עוד 46 ₪ והמשלוח יורד ל־39 ₪ · עוד 236 ₪ והוא חינם" and its shorter forms. price(cents) formats money.
  // This line wraps on phones: each amount is glued to its ₪ with a no-break space, so a line never ends in "39"
  // with the "₪" on the next one (text-wrap: balance alone only moves that split to another amount).
  function tierText(step, labels, price) {
    if (!step) return '';
    const amount = cents => String(price(cents)).replace(/ /g, ' ');
    if (step.next) {
      const parts = [fill(labels.next_tier, { amount: amount(step.next.missing), fee: amount(step.next.fee) })];
      if (step.freeMissing) parts.push(fill(labels.free_in, { amount: amount(step.freeMissing) }));
      return parts.join(' · ');
    }
    if (step.freeMissing) return fill(labels.free_in_only, { amount: amount(step.freeMissing) });
    return step.fee === 0 ? labels.free_now : '';
  }

  // The raw calculator request; lookup() and the cart both use it, so the probe is built in one place.
  function checkZone(city, { source = SOURCE, address1 = '', signal, timeout = 9000 } = {}) {
    return requestJson(`${source}/api/check-zone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      signal,
      body: JSON.stringify({ address: { city, ...(address1 ? { address1 } : {}), country: 'Israel' }, items: [], hasBigSku: false, cartTotal: PROBE_TOTAL })
    }, { attempts: 2, timeout });
  }

  function readTerms() {
    try {
      return JSON.parse(sessionStorage.getItem(TERMS_KEY) || 'null') || {};
    } catch (_) {
      return {};
    }
  }

  // City-only lookups are cached per city for 30 minutes; full addresses always ask. A failure answers 'error'.
  async function lookup(city, { source = SOURCE, address1 = '', signal, timeout = 9000 } = {}) {
    const key = cityKey(city);
    if (key.length < 2) return { state: 'unknown', canonicalCity: '', terms: null };
    const cacheKey = `${source}|${key}`;
    if (!address1) {
      const cached = readTerms()[cacheKey];
      if (cached && Date.now() - cached.time < TERMS_AGE) return cached.result;
    }
    let data;
    try {
      data = await checkZone(key, { source, address1, signal, timeout });
    } catch (error) {
      if (signal?.aborted) throw error;
      return { state: 'error', canonicalCity: '', terms: null };
    }
    const result = classify(data);
    if (!address1) {
      try {
        const all = readTerms();
        all[cacheKey] = { time: Date.now(), result };
        sessionStorage.setItem(TERMS_KEY, JSON.stringify(all));
      } catch (_) {}
    }
    return result;
  }

  // The shopper's own address, kept only in this browser; the old per-tab key is read as a fallback.
  function remembered() {
    for (const [store, key] of [[() => localStorage, ADDRESS_KEY], [() => sessionStorage, LEGACY_ADDRESS_KEY]]) {
      try {
        const value = JSON.parse(store().getItem(key) || 'null');
        if (value && typeof value === 'object') {
          return {
            mode: value.mode === 'pickup' ? 'pickup' : 'shipping',
            city: typeof value.city === 'string' ? value.city : '',
            address1: typeof value.address1 === 'string' ? value.address1 : '',
            address2: typeof value.address2 === 'string' ? value.address2 : '',
            zip: typeof value.zip === 'string' ? value.zip : ''
          };
        }
      } catch (_) {}
    }
    return null;
  }

  function remember(value) {
    try {
      localStorage.setItem(ADDRESS_KEY, JSON.stringify(value));
    } catch (_) {}
  }

  // Funnel events for the GA4 custom pixel (docs/hoya-cart-pixel.js). Analytics must never break the cart.
  function track(name, data = {}) {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (_) {}
  }

  window.HoyaZoneTerms = { SOURCE, PROBE_TOTAL, cityKey, fill, wait, pause, requestJson, loadZones, agreeing, zoneByName, daysText, formatMoney, classify, fee, tiers, tierText, checkZone, lookup, remembered, remember, track };
})();
